import { initialApi as api } from './initial-api';
export const addTagTypes = [
  'AdminService',
  'ConsentService',
  'ConversationService',
  'FeedbackService',
  'FleetService',
  'MemoryService',
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      adminServiceGetSystemHealth: build.query<
        AdminServiceGetSystemHealthApiResponse,
        AdminServiceGetSystemHealthApiArg
      >({
        query: () => ({ url: `/admin/health` }),
        providesTags: ['AdminService'],
      }),
      adminServiceGetLogs: build.query<
        AdminServiceGetLogsApiResponse,
        AdminServiceGetLogsApiArg
      >({
        query: (queryArg) => ({
          url: `/admin/logs/${queryArg.messageId}`,
          params: {
            minLevel: queryArg.minLevel,
          },
        }),
        providesTags: ['AdminService'],
      }),
      adminServiceListActiveSessions: build.query<
        AdminServiceListActiveSessionsApiResponse,
        AdminServiceListActiveSessionsApiArg
      >({
        query: () => ({ url: `/admin/sessions` }),
        providesTags: ['AdminService'],
      }),
      adminServiceGetSql: build.query<
        AdminServiceGetSqlApiResponse,
        AdminServiceGetSqlApiArg
      >({
        query: (queryArg) => ({ url: `/admin/sql/${queryArg.messageId}` }),
        providesTags: ['AdminService'],
      }),
      adminServiceGetTrace: build.query<
        AdminServiceGetTraceApiResponse,
        AdminServiceGetTraceApiArg
      >({
        query: (queryArg) => ({ url: `/admin/trace/${queryArg.traceId}` }),
        providesTags: ['AdminService'],
      }),
      consentServiceGetConsent: build.query<
        ConsentServiceGetConsentApiResponse,
        ConsentServiceGetConsentApiArg
      >({
        query: (queryArg) => ({
          url: `/consent`,
          params: {
            profileId: queryArg.profileId,
          },
        }),
        providesTags: ['ConsentService'],
      }),
      consentServiceRecordConsent: build.mutation<
        ConsentServiceRecordConsentApiResponse,
        ConsentServiceRecordConsentApiArg
      >({
        query: (queryArg) => ({
          url: `/consent`,
          method: 'POST',
          body: queryArg.recordConsentRequest,
        }),
        invalidatesTags: ['ConsentService'],
      }),
      conversationServiceListConversations: build.query<
        ConversationServiceListConversationsApiResponse,
        ConversationServiceListConversationsApiArg
      >({
        query: (queryArg) => ({
          url: `/conversations`,
          params: {
            pageSize: queryArg.pageSize,
            pageToken: queryArg.pageToken,
          },
        }),
        providesTags: ['ConversationService'],
      }),
      conversationServiceCreateConversation: build.mutation<
        ConversationServiceCreateConversationApiResponse,
        ConversationServiceCreateConversationApiArg
      >({
        query: (queryArg) => ({
          url: `/conversations`,
          method: 'POST',
          body: queryArg.createConversationRequest,
        }),
        invalidatesTags: ['ConversationService'],
      }),
      conversationServiceGetConversation: build.query<
        ConversationServiceGetConversationApiResponse,
        ConversationServiceGetConversationApiArg
      >({
        query: (queryArg) => ({
          url: `/conversations/${queryArg.conversationId}`,
        }),
        providesTags: ['ConversationService'],
      }),
      conversationServiceDeleteConversation: build.mutation<
        ConversationServiceDeleteConversationApiResponse,
        ConversationServiceDeleteConversationApiArg
      >({
        query: (queryArg) => ({
          url: `/conversations/${queryArg.conversationId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['ConversationService'],
      }),
      conversationServiceGetArtifact: build.query<
        ConversationServiceGetArtifactApiResponse,
        ConversationServiceGetArtifactApiArg
      >({
        query: (queryArg) => ({
          url: `/conversations/${queryArg.conversationId}/artifacts/${queryArg.artifactId}`,
        }),
        providesTags: ['ConversationService'],
      }),
      conversationServiceDeliverFetchResults: build.mutation<
        ConversationServiceDeliverFetchResultsApiResponse,
        ConversationServiceDeliverFetchResultsApiArg
      >({
        query: (queryArg) => ({
          url: `/conversations/${queryArg.conversationId}/fetch-results`,
          method: 'POST',
          body: queryArg.deliverFetchResultsRequest,
        }),
        invalidatesTags: ['ConversationService'],
      }),
      conversationServiceListConversationMessages: build.query<
        ConversationServiceListConversationMessagesApiResponse,
        ConversationServiceListConversationMessagesApiArg
      >({
        query: (queryArg) => ({
          url: `/conversations/${queryArg.conversationId}/messages`,
          params: {
            pageSize: queryArg.pageSize,
            pageToken: queryArg.pageToken,
          },
        }),
        providesTags: ['ConversationService'],
      }),
      conversationServiceSendMessage: build.mutation<
        ConversationServiceSendMessageApiResponse,
        ConversationServiceSendMessageApiArg
      >({
        query: (queryArg) => ({
          url: `/conversations/${queryArg.conversationId}/messages`,
          method: 'POST',
          body: queryArg.sendMessageRequest,
        }),
        invalidatesTags: ['ConversationService'],
      }),
      conversationServiceStopConversation: build.mutation<
        ConversationServiceStopConversationApiResponse,
        ConversationServiceStopConversationApiArg
      >({
        query: (queryArg) => ({
          url: `/conversations/${queryArg.conversationId}/stop`,
          method: 'POST',
        }),
        invalidatesTags: ['ConversationService'],
      }),
      feedbackServiceGetFeedback: build.query<
        FeedbackServiceGetFeedbackApiResponse,
        FeedbackServiceGetFeedbackApiArg
      >({
        query: (queryArg) => ({
          url: `/feedback`,
          params: {
            conversationId: queryArg.conversationId,
            traceId: queryArg.traceId,
          },
        }),
        providesTags: ['FeedbackService'],
      }),
      feedbackServiceSubmitFeedback: build.mutation<
        FeedbackServiceSubmitFeedbackApiResponse,
        FeedbackServiceSubmitFeedbackApiArg
      >({
        query: (queryArg) => ({
          url: `/feedback`,
          method: 'POST',
          body: queryArg.submitFeedbackRequest,
        }),
        invalidatesTags: ['FeedbackService'],
      }),
      fleetServiceListFleets: build.query<
        FleetServiceListFleetsApiResponse,
        FleetServiceListFleetsApiArg
      >({
        query: (queryArg) => ({
          url: `/fleets`,
          params: {
            pageSize: queryArg.pageSize,
            pageToken: queryArg.pageToken,
          },
        }),
        providesTags: ['FleetService'],
      }),
      fleetServiceGetFleet: build.query<
        FleetServiceGetFleetApiResponse,
        FleetServiceGetFleetApiArg
      >({
        query: (queryArg) => ({ url: `/fleets/${queryArg.fleetId}` }),
        providesTags: ['FleetService'],
      }),
      memoryServiceDeleteUserHistory: build.mutation<
        MemoryServiceDeleteUserHistoryApiResponse,
        MemoryServiceDeleteUserHistoryApiArg
      >({
        query: (queryArg) => ({
          url: `/memory/history`,
          method: 'DELETE',
          params: {
            confirm: queryArg.confirm,
            beforeDate: queryArg.beforeDate,
          },
        }),
        invalidatesTags: ['MemoryService'],
      }),
      memoryServiceGetUserProfile: build.query<
        MemoryServiceGetUserProfileApiResponse,
        MemoryServiceGetUserProfileApiArg
      >({
        query: () => ({ url: `/memory/profile` }),
        providesTags: ['MemoryService'],
      }),
      memoryServiceUpdateUserProfile: build.mutation<
        MemoryServiceUpdateUserProfileApiResponse,
        MemoryServiceUpdateUserProfileApiArg
      >({
        query: (queryArg) => ({
          url: `/memory/profile`,
          method: 'PUT',
          body: queryArg.updateUserProfileRequest,
        }),
        invalidatesTags: ['MemoryService'],
      }),
      memoryServiceGetRecentQueries: build.query<
        MemoryServiceGetRecentQueriesApiResponse,
        MemoryServiceGetRecentQueriesApiArg
      >({
        query: (queryArg) => ({
          url: `/memory/recent`,
          params: {
            limit: queryArg.limit,
          },
        }),
        providesTags: ['MemoryService'],
      }),
      memoryServiceGetQuerySuggestions: build.query<
        MemoryServiceGetQuerySuggestionsApiResponse,
        MemoryServiceGetQuerySuggestionsApiArg
      >({
        query: (queryArg) => ({
          url: `/memory/suggestions`,
          params: {
            q: queryArg.q,
            limit: queryArg.limit,
          },
        }),
        providesTags: ['MemoryService'],
      }),
      conversationServiceShare: build.mutation<
        ConversationServiceShareApiResponse,
        ConversationServiceShareApiArg
      >({
        query: (queryArg) => ({
          url: `/share`,
          method: 'POST',
          body: queryArg.shareRequest,
        }),
        invalidatesTags: ['ConversationService'],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as sidekickApi };
export type AdminServiceGetSystemHealthApiResponse =
  /** status 200 OK */ GetSystemHealthResponse;
export type AdminServiceGetSystemHealthApiArg = void;
export type AdminServiceGetLogsApiResponse =
  /** status 200 OK */ GetLogsResponse;
export type AdminServiceGetLogsApiArg = {
  /** Path parameter */
  messageId: string;
  /** Log level filter (query param): "DEBUG" | "INFO" | "WARNING" | "ERROR" */
  minLevel?: string;
};
export type AdminServiceListActiveSessionsApiResponse =
  /** status 200 OK */ ListActiveSessionsResponse;
export type AdminServiceListActiveSessionsApiArg = void;
export type AdminServiceGetSqlApiResponse = /** status 200 OK */ GetSqlResponse;
export type AdminServiceGetSqlApiArg = {
  /** Path parameter */
  messageId: string;
};
export type AdminServiceGetTraceApiResponse =
  /** status 200 OK */ GetTraceResponse;
export type AdminServiceGetTraceApiArg = {
  /** Path parameter */
  traceId: string;
};
export type ConsentServiceGetConsentApiResponse =
  /** status 200 OK */ GetConsentResponse;
export type ConsentServiceGetConsentApiArg = {
  /** The customer/user UUID consent is keyed on (query param). Required: a
     missing/empty profileId returns 400. */
  profileId?: string;
};
export type ConsentServiceRecordConsentApiResponse =
  /** status 200 OK */ RecordConsentResponse;
export type ConsentServiceRecordConsentApiArg = {
  recordConsentRequest: RecordConsentRequest;
};
export type ConversationServiceListConversationsApiResponse =
  /** status 200 OK */ ListConversationsResponse;
export type ConversationServiceListConversationsApiArg = {
  /** Pagination */
  pageSize?: number;
  pageToken?: string;
};
export type ConversationServiceCreateConversationApiResponse =
  /** status 200 OK */ CreateConversationResponse;
export type ConversationServiceCreateConversationApiArg = {
  createConversationRequest: CreateConversationRequest;
};
export type ConversationServiceGetConversationApiResponse =
  /** status 200 OK */ GetConversationResponse;
export type ConversationServiceGetConversationApiArg = {
  /** Path parameter */
  conversationId: string;
};
export type ConversationServiceDeleteConversationApiResponse =
  /** status 200 OK */ DeleteConversationResponse;
export type ConversationServiceDeleteConversationApiArg = {
  /** Path parameter */
  conversationId: string;
};
export type ConversationServiceGetArtifactApiResponse =
  /** status 200 OK */ GetArtifactResponse;
export type ConversationServiceGetArtifactApiArg = {
  /** Path parameters */
  conversationId: string;
  artifactId: string;
};
export type ConversationServiceDeliverFetchResultsApiResponse =
  /** status 200 OK */ DeliverFetchResultsResponse;
export type ConversationServiceDeliverFetchResultsApiArg = {
  /** Path parameter */
  conversationId: string;
  deliverFetchResultsRequest: DeliverFetchResultsRequest;
};
export type ConversationServiceListConversationMessagesApiResponse =
  /** status 200 OK */ ListConversationMessagesResponse;
export type ConversationServiceListConversationMessagesApiArg = {
  /** Path parameter */
  conversationId: string;
  /** Pagination (default page_size: 50, max: 100) */
  pageSize?: number;
  pageToken?: string;
};
export type ConversationServiceSendMessageApiResponse =
  /** status 200 OK */ SendMessageResponse;
export type ConversationServiceSendMessageApiArg = {
  /** Path parameter */
  conversationId: string;
  sendMessageRequest: SendMessageRequest;
};
export type ConversationServiceStopConversationApiResponse =
  /** status 200 OK */ StopConversationResponse;
export type ConversationServiceStopConversationApiArg = {
  /** Path parameter */
  conversationId: string;
};
export type FeedbackServiceGetFeedbackApiResponse =
  /** status 200 OK */ GetFeedbackResponse;
export type FeedbackServiceGetFeedbackApiArg = {
  /** Filter by conversation ID (query param) */
  conversationId?: string;
  /** Filter by trace ID (query param) */
  traceId?: string;
};
export type FeedbackServiceSubmitFeedbackApiResponse =
  /** status 200 OK */ SubmitFeedbackResponse;
export type FeedbackServiceSubmitFeedbackApiArg = {
  submitFeedbackRequest: SubmitFeedbackRequest;
};
export type FleetServiceListFleetsApiResponse =
  /** status 200 OK */ ListFleetsResponse;
export type FleetServiceListFleetsApiArg = {
  /** Pagination (optional, for users with many fleets) */
  pageSize?: number;
  pageToken?: string;
};
export type FleetServiceGetFleetApiResponse =
  /** status 200 OK */ GetFleetResponse;
export type FleetServiceGetFleetApiArg = {
  /** Path parameter */
  fleetId: string;
};
export type MemoryServiceDeleteUserHistoryApiResponse =
  /** status 200 OK */ DeleteUserHistoryResponse;
export type MemoryServiceDeleteUserHistoryApiArg = {
  /** Confirmation required (query param) */
  confirm?: boolean;
  /** Optional: only delete before this date (query param) */
  beforeDate?: string;
};
export type MemoryServiceGetUserProfileApiResponse =
  /** status 200 OK */ GetUserProfileResponse;
export type MemoryServiceGetUserProfileApiArg = void;
export type MemoryServiceUpdateUserProfileApiResponse =
  /** status 200 OK */ UpdateUserProfileResponse;
export type MemoryServiceUpdateUserProfileApiArg = {
  updateUserProfileRequest: UpdateUserProfileRequest;
};
export type MemoryServiceGetRecentQueriesApiResponse =
  /** status 200 OK */ GetRecentQueriesResponse;
export type MemoryServiceGetRecentQueriesApiArg = {
  /** Query param, default: 10 */
  limit?: number;
};
export type MemoryServiceGetQuerySuggestionsApiResponse =
  /** status 200 OK */ GetQuerySuggestionsResponse;
export type MemoryServiceGetQuerySuggestionsApiArg = {
  /** Partial query text (query param) */
  q?: string;
  /** Maximum suggestions to return (query param) */
  limit?: number;
};
export type ConversationServiceShareApiResponse =
  /** status 200 OK */ ShareResponse;
export type ConversationServiceShareApiArg = {
  shareRequest: ShareRequest;
};
export type ServiceHealth = {
  service?: string;
  status?: string;
  latencyMs?: number;
  lastCheck?: string;
  error?: string;
};
export type GetSystemHealthResponse = {
  overallStatus?: string;
  services?: ServiceHealth[];
  version?: string;
  environment?: string;
};
export type GoogleProtobufAny = {
  /** The type of the serialized message. */
  '@type'?: string;
  [key: string]: unknown;
};
export type Status = {
  /** The status code, which should be an enum value of [google.rpc.Code][google.rpc.Code]. */
  code?: number;
  /** A developer-facing error message, which should be in English. Any user-facing error message should be localized and sent in the [google.rpc.Status.details][google.rpc.Status.details] field, or localized by the client. */
  message?: string;
  /** A list of messages that carry the error details.  There is a common set of message types for APIs to use. */
  details?: GoogleProtobufAny[];
};
export type LogEntry = {
  timestamp?: string;
  level?: string;
  component?: string;
  message?: string;
  context?: {
    [key: string]: string;
  };
};
export type GetLogsResponse = {
  logs?: LogEntry[];
};
export type ActiveSession = {
  sessionId?: string;
  userId?: string;
  organizationId?: string;
  startedAt?: string;
  lastActivityAt?: string;
  messageCount?: number;
};
export type ListActiveSessionsResponse = {
  sessions?: ActiveSession[];
  totalActive?: number;
};
export type GetSqlResponse = {
  /** The generated SQL query */
  sql?: string;
  /** SQL validation warnings (if any) */
  warnings?: string[];
  /** Whether SQL was validated successfully */
  validated?: boolean;
  /** Execution time in milliseconds */
  executionTimeMs?: number;
  /** Row count returned */
  rowCount?: number;
  /** Tables accessed */
  tablesAccessed?: string[];
};
export type TraceSpan = {
  spanId?: string;
  name?: string;
  parentSpanId?: string;
  startTime?: string;
  endTime?: string;
  durationMs?: number;
  attributes?: {
    [key: string]: string;
  };
  status?: string;
};
export type GetTraceResponse = {
  traceId?: string;
  conversationId?: string;
  spans?: TraceSpan[];
  /** Summary metrics */
  totalDurationMs?: number;
  llmCalls?: number;
  sqlQueries?: number;
  ragRetrievals?: number;
};
export type ConsentTerms = {
  /** Terms code, e.g. "AI_MONITORING_TC" */
  code?: string;
  /** Terms revision, e.g. "20260617" */
  revision?: string;
  /** Human-readable title (may be empty) */
  title?: string;
  /** Link to the full terms document (may be empty) */
  url?: string;
};
export type GetConsentResponse = {
  /** Consent status: "ACCEPTED" | "NOT_ACCEPTED" | "UNKNOWN"
     "UNKNOWN" indicates a degraded read (CLPT unreachable). */
  status?: string;
  /** Stored decision if any: "ACCEPT" | "DECLINE" (null when none/unknown) */
  decision?: string;
  /** When the stored decision was recorded (ISO-8601, null when none) */
  decisionDateTime?: string;
  /** Terms to render on the consent form (authoritative from CLTM, or the
     configured fallback terms when CLTM is unreachable). */
  currentTerms?: ConsentTerms;
  /** True when the response is degraded (CLPT/CLTM unreachable or returned no
     terms); the form should still render and the caller should proceed. */
  degraded?: boolean;
};
export type RecordConsentResponse = {
  /** Always true when the request was accepted by the service. */
  success?: boolean;
  /** True when the decision was written to CLPT; false when it was only
     audited (CLPT disabled/unreachable, or authoritative terms unavailable). */
  persisted?: boolean;
  /** The decisionDateTime written to CLPT (ISO-8601, null when not persisted). */
  decisionDateTime?: string;
  /** True when persistence was degraded (decision not durably stored in CLPT). */
  degraded?: boolean;
};
export type RecordConsentRequest = {
  /** The customer/user UUID consent is keyed on. Required. */
  profileId: string;
  /** The consent decision: "ACCEPT" | "DECLINE". Required. */
  decision: string;
};
export type ConversationSummary = {
  conversationId?: string;
  /** First user message (truncated to 100 chars) */
  title?: string;
  /** Message count */
  messageCount?: number;
  createdAt?: string;
  updatedAt?: string;
};
export type PaginationResponse = {
  /** Cursor for next page (empty if no more pages) */
  nextPageToken?: string;
  /** Total count of items (if available, -1 if unknown) */
  totalCount?: number;
};
export type ListConversationsResponse = {
  conversations?: ConversationSummary[];
  pagination?: PaginationResponse;
};
export type CreateConversationResponse = {
  /** Unique conversation identifier (UUID) */
  conversationId?: string;
  /** When the conversation was created */
  createdAt?: string;
};
export type CreateConversationRequest = {
  /** Optional: metadata to attach to conversation */
  metadata?: {
    [key: string]: string;
  };
  /** Optional: the message id of a stored question this conversation replaces.
     The new conversation is a branch that inherits the exchanges before it.
     An id naming no question the caller holds is NOT_FOUND. */
  editedMessageId?: string;
};
export type GetConversationResponse = {
  conversationId?: string;
  /** Conversation metadata */
  metadata?: {
    [key: string]: string;
  };
  /** Message count (total messages in conversation) */
  messageCount?: number;
  /** Timestamps */
  createdAt?: string;
  updatedAt?: string;
};
export type DeleteConversationResponse = {
  success?: boolean;
};
export type GetArtifactResponse = {
  artifactId?: string;
  /** "table" | "chart" */
  kind?: string;
  /** The table's title; empty for a chart and for an untitled table */
  title?: string;
  /** What the download answers: "text/csv" | "image/png" */
  contentType?: string;
  /** Rows the table's download holds; 0 for a chart */
  rowCount?: number;
  /** The chart image, signed when this was read; empty for a table */
  url?: string;
  /** When url stops opening (ISO 8601); empty for a table */
  expiresAt?: string;
};
export type DeliverFetchResultsResponse = {
  /** Always true: results no turn is waiting for are refused with NOT_FOUND */
  delivered?: boolean;
};
export type DeliverFetchResultsRequest = {
  /** Path parameter */
  conversationId?: string;
  /** One entry per step of the FetchPlan, in plan order, as a JSON array. The
     relay carries the entries to the AI service without reading them. */
  stepsJson?: string;
  /** The plan_id of the FetchPlan these results answer */
  planId?: string;
};
export type Citation = {
  /** 1-based citation number; matches the inline [N] marker in Message.content
     and the position of this citation in the parent `citations` list. */
  index?: number;
  /** Customer-facing link text, e.g. "2026 Owner Manual for the GMC Hummer EV".
     Built by the agent from chunk metadata; safe to render verbatim. */
  displayLabel?: string;
  /** Verbose debug breadcrumb (filename + page), e.g.
     "2026 GMC Hummer EV - 26_GMC_Hummer_EV_OM_en_US_U_19344615A_2025JUN13.pdf (page 216)".
     Intended for admin/debug surfaces, not customer-facing rendering. */
  label?: string;
  /** Raw source filename, e.g.
     "26_GMC_Hummer_EV_OM_en_US_U_19344615A_2025JUN13.pdf". */
  source?: string;
  /** Page number in the source document. Proto3 `optional` so the wire can
     distinguish "unknown" from a real 0 (some documents have a page-0 cover);
     clients should treat unset as "unknown". */
  page?: number;
  /** Vehicle metadata associated with the chunk, e.g. "2026 GMC Hummer EV".
     Empty string when the chunk has no vehicle metadata. */
  vehicle?: string;
  /** Directly-fetchable signed Azure Blob/SAS URL for the source document,
     with `#page=N` appended when applicable. Empty string when the document
     linker is disabled or URL minting failed. */
  url?: string;
};
export type ArtifactRef = {
  /** Artifact identifier */
  id?: string;
  /** "table" | "chart" */
  kind?: string;
  /** The table's title; empty for a chart and for an untitled table */
  title?: string;
  /** Rows the stored table holds; 0 for a chart */
  rowCount?: number;
};
export type Message = {
  /** Unique message identifier */
  id?: string;
  /** Conversation this message belongs to */
  conversationId?: string;
  /** Role: "user" | "assistant" | "system" | "tool" */
  role?: string;
  /** Message content (text) */
  content?: string;
  /** Optional: agent/tool name that produced this message */
  name?: string;
  /** When the message was created (ISO 8601) */
  createdAt?: string;
  /** Optional: metadata for debugging/admin */
  metadata?: {
    [key: string]: string;
  };
  /** Image URLs for visualizations the agent rendered alongside this
     message. Today these are charts (bar/line/pie/heatmap/etc.), but the
     field is intentionally broader -- table snapshots, diagrams, or map
     tiles are all valid in principle. Each entry is a directly-fetchable
     PNG URL (signed Azure Blob / Front Door URLs with limited-lifetime
     SAS query strings). Empty when the assistant did not produce a
     visualization. Clients should treat the list as ordered: index 0 is
     the primary visualization, additional entries are supplemental.
     See also: VisualizationData / VisualizationEvent for the streaming
     counterpart used over the SSE event channel. */
  visualizationUrls?: string[];
  /** Structured RAG citations referenced by inline [N] markers in `content`,
     ordered by index (1-based, so `citations[N-1]` matches the `[N]` marker).
     Empty for non-RAG answers. The assistant's text body keeps the [N]
     markers verbatim so clients can render them as links to the matching
     citation entry. The upstream agent also emits a parallel `sources`
     list[str] of "[N] {display_label}" strings, but we intentionally omit
     it here: each citation already carries `display_label`, so clients can
     synthesize the same string without a redundant wire field. */
  citations?: Citation[];
  /** The message before this one in the conversation; empty for the first
     question. */
  parentId?: string;
  /** The messages that follow this one; empty for the last message. */
  childrenIds?: string[];
  /** The tables and charts this answer showed, in the order it showed them.
     Empty for a question and for an answer with nothing to download. */
  artifacts?: ArtifactRef[];
};
export type ListConversationMessagesResponse = {
  /** Messages, oldest first */
  messages?: Message[];
  /** Pagination info */
  pagination?: PaginationResponse;
};
export type VisualizationData = {
  /** Visualization type: "bar" | "line" | "pie" | "table" | "heatmap" */
  type?: string;
  /** Title for the visualization */
  title?: string;
  /** Chart configuration (frontend-specific, JSON-encoded)
     e.g., axis labels, colors, legend position */
  configJson?: string;
  /** The actual data points (JSON array)
     Format depends on type:
       bar/line: [{"x": "label", "y": value}, ...]
       pie: [{"label": "slice", "value": number}, ...]
       table: [{"col1": val, "col2": val}, ...] */
  dataJson?: string;
};
export type VisualizationEvent = {
  /** Visualization data for rendering */
  visualization?: VisualizationData;
  /** Position hint: "inline" | "sidebar" | "modal" */
  position?: string;
};
export type SendMessageResponse = {
  /** The user message that was created */
  messageId?: string;
  /** Conversation ID (echoed back) */
  conversationId?: string;
  /** Timestamp when message was accepted */
  acceptedAt?: string;
  /** Populated when client requests synchronous response (Accept: application/json).
     Empty when client uses streaming (Accept: text/event-stream). */
  assistantMessage?: Message;
  /** MLflow trace ID for feedback association (synchronous mode only). */
  traceId?: string;
  /** Structured visualization payload that accompanies the assistant message
     (synchronous mode only). Mirrors the VisualizationEvent emitted on the
     SSE stream so the frontend can share parsing logic across both surfaces.
     Empty when the assistant did not produce a structured visualization
     (text-only answers, errors, RAG-only answers).
     Today this is populated for multi-row NL2SQL results as a "table"
     visualization; the field is intentionally typed to VisualizationEvent
     so future variants (chart payloads beyond visualization_urls) can ride
     the same surface without a wire change. */
  visualization?: VisualizationEvent;
};
export type ActionConfirmation = {
  /** The token from the PendingAction event, returned unchanged. The relay does
     not verify it: an expired or invalid token is still delivered, and the
     authorization service is the only verifier. */
  token?: string;
  /** True to proceed, false to decline. A decline is delivered rather than
     dropped, so the AI service can end the proposal and say so. */
  approved?: boolean;
};
export type SendMessageRequest = {
  /** Path parameter */
  conversationId?: string;
  /** User's message content */
  content?: string;
  /** Input type: "text" | "voice" */
  inputType?: string;
  /** Optional: override default model behavior */
  modelHint?: string;
  /** Optional: prompt alias for testing different prompts */
  promptAlias?: string;
  /** Optional: interface identifier for experiment routing */
  interface?: string;
  /** Optional: fleet UUID to scope the answer to a single fleet. Applied per
     message so the caller can switch fleets between turns. Absent means the
     query is not fleet-scoped. This is a non-authoritative filter; the
     authoritative tenant (organization_id) is derived server-side from the
     caller's identity and is never read from this request body. */
  fleetId?: string;
  /** Optional: the caller's answer to an action this conversation proposed on an
     earlier turn. Absent on an ordinary turn. Requires the streaming response
     format, since the resulting directive rides a StreamEvent arm. */
  actionConfirmation?: ActionConfirmation;
};
export type StopConversationResponse = {
  /** Always true: a stop that reached no running turn is also a success */
  stopped?: boolean;
};
export type FeedbackEntry = {
  feedbackId?: string;
  traceId?: string;
  conversationId?: string;
  thumbsUp?: boolean;
  rationale?: string;
  category?: string;
  userId?: string;
  createdAt?: string;
};
export type GetFeedbackResponse = {
  feedback?: FeedbackEntry[];
};
export type SubmitFeedbackResponse = {
  success?: boolean;
  /** Feedback ID for reference */
  feedbackId?: string;
};
export type SubmitFeedbackRequest = {
  /** MLflow trace ID (from StreamDone.trace_id)
     Links feedback to a specific response */
  traceId?: string;
  /** True = thumbs up (positive), False = thumbs down (negative) */
  thumbsUp?: boolean;
  /** Optional: user's explanation for the feedback */
  rationale?: string;
  /** Optional: conversation ID for context */
  conversationId?: string;
  /** Optional: specific message ID if feedback is for one message */
  messageId?: string;
  /** Optional: feedback category for negative feedback
     "incorrect" | "incomplete" | "slow" | "confusing" | "other" */
  category?: string;
};
export type Fleet = {
  /** Unique fleet identifier */
  fleetId?: string;
  /** Human-readable fleet name */
  name?: string;
  /** Optional description */
  description?: string;
  /** Vehicle count in this fleet */
  vehicleCount?: number;
  /** Fleet metadata (e.g., region, business unit) */
  metadata?: {
    [key: string]: string;
  };
};
export type ListFleetsResponse = {
  fleets?: Fleet[];
  /** Pagination info */
  pagination?: PaginationResponse;
  /** Total fleet count for this user */
  totalCount?: number;
};
export type GetFleetResponse = {
  fleet?: Fleet;
};
export type DeleteUserHistoryResponse = {
  success?: boolean;
  conversationsDeleted?: number;
  messagesDeleted?: number;
};
export type UserPreferences = {
  /** Display preferences (PRD R11) */
  theme?: string;
  units?: string;
  dateFormat?: string;
  timezone?: string;
  /** Notification preferences */
  showPushedInsights?: boolean;
  emailSummaries?: boolean;
};
export type GetUserProfileResponse = {
  userId?: string;
  organizationId?: string;
  preferences?: UserPreferences;
  /** Statistics */
  totalConversations?: number;
  totalMessages?: number;
  lastActiveAt?: string;
  /** Most frequent query terms (for autocomplete) */
  frequentTerms?: string[];
};
export type UpdateUserProfileResponse = {
  success?: boolean;
  updatedPreferences?: UserPreferences;
};
export type UpdateUserProfileRequest = {
  /** Partial update - only set fields are updated */
  preferences?: UserPreferences;
};
export type RecentQuery = {
  query?: string;
  conversationId?: string;
  timestamp?: string;
};
export type GetRecentQueriesResponse = {
  queries?: RecentQuery[];
};
export type QuerySuggestion = {
  /** Suggested query text */
  text?: string;
  /** Source: "history" | "popular" | "template" */
  source?: string;
  /** Relevance score (0.0 to 1.0) */
  score?: number;
  /** When this query was last used (if from history) */
  lastUsed?: string;
};
export type GetQuerySuggestionsResponse = {
  suggestions?: QuerySuggestion[];
};
export type ShareAttachment = {
  /** The name the table or chart downloads as */
  name?: string;
  /** "text/csv" | "image/png" */
  type?: string;
  content?: string;
};
export type ShareEmail = {
  /** The sender's address alone */
  to?: string[];
  /** The recipients, less any matching the sender's address */
  bcc?: string[];
  /** The sender's address */
  replyTo?: string;
  /** The address the email is sent from */
  sender?: string;
  /** "ai-sidekick-api" */
  templateNamespace?: string;
  /** "share-insight" */
  templateName?: string;
  /** "en-US" */
  templateLocale?: string;
  /** A table's CSV or a chart's PNG; empty for a response or conversation share */
  fileAttachments?: ShareAttachment[];
};
export type ShareResponse = {
  shareId?: string;
  /** The addresses the share names, the sender's own included */
  recipientCount?: number;
  /** The request the authorization service posts to communications-api */
  email?: ShareEmail;
};
export type ShareRecipients = {
  emails?: string[];
};
export type ShareSender = {
  /** Empty when the token carries no name */
  name?: string;
  email?: string;
};
export type ShareRequest = {
  conversationId?: string;
  /** The message shared; any message of the conversation for a conversation share */
  messageId?: string;
  /** "response" | "table" | "visualization" | "conversation" */
  shareType?: string;
  recipients?: ShareRecipients;
  /** Set by the authorization service from the caller's token */
  sender?: ShareSender;
  /** Exactly one table for a table share, exactly one chart for a
     visualization share, and none otherwise */
  artifactIds?: string[];
  /** At most 1,000 characters */
  note?: string;
};
export const {
  useAdminServiceGetSystemHealthQuery,
  useAdminServiceGetLogsQuery,
  useAdminServiceListActiveSessionsQuery,
  useAdminServiceGetSqlQuery,
  useAdminServiceGetTraceQuery,
  useConsentServiceGetConsentQuery,
  useConsentServiceRecordConsentMutation,
  useConversationServiceListConversationsQuery,
  useConversationServiceCreateConversationMutation,
  useConversationServiceGetConversationQuery,
  useConversationServiceDeleteConversationMutation,
  useConversationServiceGetArtifactQuery,
  useConversationServiceDeliverFetchResultsMutation,
  useConversationServiceListConversationMessagesQuery,
  useConversationServiceSendMessageMutation,
  useConversationServiceStopConversationMutation,
  useFeedbackServiceGetFeedbackQuery,
  useFeedbackServiceSubmitFeedbackMutation,
  useFleetServiceListFleetsQuery,
  useFleetServiceGetFleetQuery,
  useMemoryServiceDeleteUserHistoryMutation,
  useMemoryServiceGetUserProfileQuery,
  useMemoryServiceUpdateUserProfileMutation,
  useMemoryServiceGetRecentQueriesQuery,
  useMemoryServiceGetQuerySuggestionsQuery,
  useConversationServiceShareMutation,
} = injectedRtkApi;
